import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import qs from 'qs'
import { showSectionSelectionModal } from '../../actions/modals'
import SearchProviderContainer from './SearchProviderContainer'
import Button from '../../components/uiKit/Button'
import TopPageHeader from '../../components/pageHeaders/TopPageHeader'
import StickyPageHeader from '../../components/pageHeaders/StickyPageHeader'
import SearchResultsCard from '../../components/search/SearchResultsCard'
import SearchResultsFolderCard from '../../components/search/SearchResultsFolderCard'
import SearchResultsRepoCard from '../../components/search/SearchResultsRepoCard'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getHitKind } from '../../utils/search/getHitKind'
import { useSearchContext } from '../../services/search/SearchContext'
import analytics from '../../utils/cdpAnalytics'

// Page at /searchResults?query=searchTerm
// Get to this page by typing in search bar in top nav and clicking "see all results for X"

const updateAfter = 700

const createURL = (state) => `?${qs.stringify({ query: state.query })}`

const searchStateToUrl = (props, searchState) => (searchState ? `${props.location.pathname}${createURL(searchState)}` : '')

const urlToSearchState = (location) => qs.parse(location.search.slice(1))

// ─── Inner components (read from SearchContext, no Algolia imports) ────────────

function SearchInput() {
  const { currentRefinement, refine } = useSearchContext()
  return (
    <input
      type='text'
      placeholder='Search'
      value={currentRefinement}
      onChange={(e) => refine(e.target.value)}
      autoComplete='off'
      className='topBar-search'
      id='searchInputField'
      onKeyDown={(e) => {
        if (e.keyCode === 13) {
          document.getElementById('searchInputField').blur()
        }
      }}
    />
  )
}

const Hit = withRouter(({ item, play }) => {
  const kind = getHitKind(item)
  if (kind === 'set') {
    return (
      <SearchResultsCard
        set
        item={item}
        title={item.name}
        play={() => { play(item) }}
      />
    )
  }
  if (kind === 'question') {
    return (
      <SearchResultsCard
        question
        item={item}
        title={getQuestionBodyLabel(item)}
        play={() => { play(item) }}
      />
    )
  }
  if (kind === 'folder') {
    return (
      <SearchResultsFolderCard
        folder={item}
      />
    )
  }
  if (kind === 'repo') {
    return (
      <SearchResultsRepoCard
        repo={item}
      />
    )
  }
  return null
})

function IndexHitList({ indexName, play }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { hits, hasMore, loadMore } = indexResults[indexName] || {}
  if (!currentRefinement || !hits || hits.length === 0) return null
  return (
    <div className='searchResults-list'>
      <main id='hits'>
        {hits.map((hit) => <Hit item={hit} key={hit.objectID} play={play} />)}
        {hasMore && (
          <div className='searchResults-list-showMoreContainer'>
            <Button
              label='Show more results'
              size='large'
              onClickFunction={loadMore}
            />
          </div>
        )}
      </main>
    </div>
  )
}

function SectionHeader({ indexName, label }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { nbHits } = indexResults[indexName] || {}
  if (!currentRefinement || !nbHits) return null
  return (
    <div className={`searchResults-subHeader searchResults-subHeader--${indexName}`}>
      {label}
    </div>
  )
}

function ResultsPageHeader() {
  const { indexResults, currentRefinement } = useSearchContext()
  const { repos, folders, sets_questions: setsQuestions } = indexResults
  const total = (repos?.nbHits || 0) + (folders?.nbHits || 0) + (setsQuestions?.nbHits || 0)

  let searchResultsText
  if (currentRefinement) {
    if (total === 1) {
      searchResultsText = `${total} Search Result`
    } else if (total > 1) {
      searchResultsText = `${total} Search Results`
    } else {
      searchResultsText = 'No Results in Library'
    }
  }

  return [
    <TopPageHeader
      key='topHeader'
      location='searchResults'
      header={<SearchInput />}
      headerInfoRow={searchResultsText}
    />,
    <StickyPageHeader
      key='stickyHeader'
      location='searchResults'
      header={currentRefinement}
      actionGroup={searchResultsText}
    />,
  ]
}

// ─── SearchResultsContainer ───────────────────────────────────────────────────

class SearchResultsContainer extends Component {
  constructor(props) {
    super(props)
    this.onSearchStateChange = this.onSearchStateChange.bind(this)
    this.state = {
      searchState: urlToSearchState(props.location),
    }
  }

  componentDidMount() {
    analytics.page('Search Results')
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.location !== this.props.location) {
      this.setState({ searchState: urlToSearchState(props.location) })
    }
  }

  onSearchStateChange(searchState) {
    clearTimeout(this.debouncedSetState)
    this.debouncedSetState = setTimeout(() => {
      window.history.pushState(
        searchState,
        null,
        searchStateToUrl(this.props, searchState),
      )
    }, updateAfter)
    this.setState({ searchState })
  }

  render() {
    return (
      <DocumentTitle title='Search Results - Plickers'>
        <div className='page searchResults page--wideLayout'>
          <div className='page-leftPanel' />
          <div className='page-centerPanel'>
            <SearchProviderContainer
              searchState={this.state.searchState}
              onSearchStateChange={this.onSearchStateChange}
              createURL={createURL}
              hitsPerPage={100}
            >
              <ResultsPageHeader />
              <SectionHeader indexName='repos' label='Packs' />
              <IndexHitList indexName='repos' />
              <SectionHeader indexName='folders' label='Folders' />
              <IndexHitList indexName='folders' />
              <SectionHeader indexName='sets_questions' label='Items' />
              <IndexHitList indexName='sets_questions' play={this.props.showSectionSelectionModal} />
            </SearchProviderContainer>
          </div>
          <div className='page-rightPanel' />
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(connect(
  (state) => ({
    permissions: state.permissions, // pass permissions as props to ensure view updates once permissions load
  }),
  { showSectionSelectionModal },
)(SearchResultsContainer))
